import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { formatCents, type PaymentMethod } from '@pirouette/shared';
import {
  useFamilyCharges,
  useFamilyBalance,
  useFamilyPayments,
  useFamilyAccounts,
  useFamilyAccountBalances,
  useCreateBillingAccount,
  useVoidCharge,
  useRecordManualPayment,
  type ChargeWithAllocations,
} from '@/data/billing';
import { useFamilyPaymentFailures, useResolvePaymentFailure } from '@/data/stripe';
import { useFamily } from '@/data/families';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/billing/$familyId')({
  component: FamilyLedger,
});

function FamilyLedger() {
  const { familyId } = Route.useParams();
  const family = useFamily(familyId);
  const balance = useFamilyBalance(familyId);
  const charges = useFamilyCharges(familyId);
  const payments = useFamilyPayments(familyId);
  const voidCharge = useVoidCharge();
  const recordPayment = useRecordManualPayment();
  const failures = useFamilyPaymentFailures(familyId);
  const resolveFailure = useResolvePaymentFailure();
  // Sprint 12 — per-account balances + account creation.
  const accounts = useFamilyAccounts(familyId);
  const accountBalances = useFamilyAccountBalances(familyId);
  const createAccount = useCreateBillingAccount();

  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentAccountId, setPaymentAccountId] = useState<string>('');
  const [allocations, setAllocations] = useState<Record<string, number>>({});

  async function handleRecordPayment() {
    const amountCents = Math.round(parseFloat(paymentAmount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      toast({ title: 'Enter a valid amount', variant: 'error' });
      return;
    }
    if (!paymentAccountId) {
      toast({ title: 'Pick a billing account', variant: 'error' });
      return;
    }
    const allocList = Object.entries(allocations)
      .map(([charge_id, amount_cents]) => ({ charge_id, amount_cents }))
      .filter((a) => a.amount_cents > 0);
    const allocTotal = allocList.reduce((s, a) => s + a.amount_cents, 0);
    if (allocTotal !== amountCents) {
      toast({
        title: 'Allocation mismatch',
        description: `Allocations total ${formatCents(allocTotal)} but payment is ${formatCents(amountCents)}.`,
        variant: 'error',
      });
      return;
    }
    try {
      await recordPayment.mutateAsync({
        input: {
          family_id: familyId,
          account_id: paymentAccountId,
          amount_cents: amountCents,
          method: paymentMethod,
          reference: paymentReference.trim() || undefined,
        },
        charge_allocations: allocList,
      });
      setShowRecordPayment(false);
      setPaymentAmount('');
      setPaymentReference('');
      setPaymentAccountId('');
      setAllocations({});
      toast({ title: 'Payment recorded', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  const openCharges = (charges.data ?? []).filter(
    (c) => c.status === 'pending' || c.status === 'partial',
  );
  const allocTotal = Object.values(allocations).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/billing"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Billing
        </Link>
        <h1 className="mt-2">{family.data?.primary_guardian_name ?? 'Family'}</h1>
        <p className="mt-1 text-[var(--color-ink-muted)]">{family.data?.billing_email ?? '—'}</p>
      </header>

      {/* Sprint 12 — per-account balance cards. Replaces the old single
         family-total card. Each account is a separate ledger with its
         own outstanding / open / overdue. */}
      <section aria-labelledby="balances-heading">
        <div className="mb-4 flex items-baseline justify-between">
          <h2
            id="balances-heading"
            className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase"
          >
            Account balances · total {formatCents(balance.data?.outstanding_cents ?? 0)} outstanding
          </h2>
          {!(accountBalances.data ?? []).some((a) => a.kind === 'competition') && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                createAccount.mutate({
                  family_id: familyId,
                  kind: 'competition',
                  name: 'Competition',
                })
              }
              disabled={createAccount.isPending}
            >
              + Add competition account
            </Button>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(accountBalances.data ?? []).map((a) => (
            <article
              key={a.account_id}
              className="surface-elevated p-5"
              aria-label={`${a.name} balance`}
            >
              <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-xs)] uppercase">
                {a.name}
              </p>
              <p
                className="mt-2 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums"
                style={{
                  color: a.outstanding_cents > 0 ? 'var(--color-danger)' : 'var(--color-success)',
                }}
              >
                {formatCents(a.outstanding_cents)}
              </p>
              <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                {a.open_count} open
                {a.overdue_count > 0 && ` · ${a.overdue_count} overdue`}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="charges-heading">
        <div className="flex items-baseline justify-between">
          <h2 id="charges-heading" className="text-[var(--text-xl)]">
            Charges
          </h2>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowRecordPayment((v) => !v)}
              disabled={openCharges.length === 0 && !showRecordPayment}
            >
              {showRecordPayment ? 'Cancel' : 'Record payment'}
            </Button>
          </div>
        </div>

        {showRecordPayment && (
          <div className="surface-elevated mt-4 grid gap-4 p-5">
            {/* Sprint 12 — account picker is required. Payments must
               belong to one account; allocations can only target charges
               in the same account (DB-enforced). */}
            <div>
              <Label htmlFor="p-account">Billing account</Label>
              <select
                id="p-account"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                value={paymentAccountId}
                onChange={(e) => {
                  setPaymentAccountId(e.target.value);
                  setAllocations({}); // reset — different account = different charges
                }}
              >
                <option value="">Select an account…</option>
                {(accounts.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.kind}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="p-amount">Amount</Label>
                <Input
                  id="p-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="125.00"
                />
              </div>
              <div>
                <Label htmlFor="p-method">Method</Label>
                <select
                  id="p-method"
                  className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="p-ref">Reference</Label>
                <Input
                  id="p-ref"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="check #1234"
                />
              </div>
            </div>

            <div>
              <p className="mb-2 font-medium text-[var(--text-sm)]">
                Allocate to charges{paymentAccountId ? ' (this account only)' : ''}:
              </p>
              {!paymentAccountId ? (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  Pick a billing account above to see its open charges.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {openCharges
                    .filter((c) => c.account_id === paymentAccountId)
                    .map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border)] p-2"
                      >
                        <span className="text-[var(--text-sm)]">
                          {c.description}{' '}
                          <span className="text-[var(--color-ink-muted)]">
                            ({formatCents(c.remaining_cents)} remaining)
                          </span>
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-32"
                          placeholder="0.00"
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            const cents = Number.isFinite(v) ? Math.round(v * 100) : 0;
                            setAllocations((prev) => ({ ...prev, [c.id]: cents }));
                          }}
                        />
                      </li>
                    ))}
                </ul>
              )}
              <p className="mt-2 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                Allocated {formatCents(allocTotal)} ·{' '}
                {paymentAmount
                  ? `target ${formatCents(Math.round(parseFloat(paymentAmount) * 100))}`
                  : 'enter an amount above'}
              </p>
            </div>

            <Button
              onClick={() => void handleRecordPayment()}
              disabled={!paymentAmount || recordPayment.isPending}
            >
              {recordPayment.isPending ? 'Saving…' : 'Record payment'}
            </Button>
          </div>
        )}

        {charges.data?.length === 0 && (
          <p className="mt-4 text-[var(--color-ink-muted)]">No charges yet.</p>
        )}

        {charges.data && charges.data.length > 0 && (
          <ul className="mt-4 grid gap-2">
            {charges.data.map((c) => (
              <li key={c.id} className="surface flex items-center justify-between p-3">
                <div>
                  <p className="font-medium">{c.description}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{c.category}</Badge>{' '}
                    {c.due_date ? `due ${c.due_date}` : 'no due date'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium tabular-nums">{formatCents(c.amount_cents)}</span>
                  <ChargeStatusBadge charge={c} />
                  {c.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => voidCharge.mutate({ id: c.id, family_id: familyId })}
                    >
                      Void
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(failures.data ?? []).filter((f) => !f.resolved_at).length > 0 && (
        <section aria-labelledby="failures-heading">
          <h2 id="failures-heading" className="text-[var(--text-xl)]">
            Payment issues
          </h2>
          <ul className="mt-4 grid gap-2">
            {(failures.data ?? [])
              .filter((f) => !f.resolved_at)
              .map((f) => (
                <li
                  key={f.id}
                  className="surface flex items-center justify-between border-l-4 border-[var(--color-danger)] p-3"
                >
                  <div>
                    <p className="font-medium">
                      {f.amount_cents != null ? formatCents(f.amount_cents) : '—'}{' '}
                      <span className="text-[var(--color-ink-muted)]">
                        · {new Date(f.created_at).toLocaleDateString()}
                      </span>
                    </p>
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {f.failure_code ? `${f.failure_code} — ` : ''}
                      {f.failure_message ?? 'unspecified failure'}
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => resolveFailure.mutate({ id: f.id, family_id: familyId })}
                    disabled={resolveFailure.isPending}
                  >
                    Mark resolved
                  </Button>
                </li>
              ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="payments-heading">
        <h2 id="payments-heading" className="text-[var(--text-xl)]">
          Payments
        </h2>
        {payments.data?.length === 0 && (
          <p className="mt-4 text-[var(--color-ink-muted)]">No payments recorded.</p>
        )}
        {payments.data && payments.data.length > 0 && (
          <ul className="mt-4 grid gap-2">
            {payments.data.map((p) => (
              <li key={p.id} className="surface flex items-center justify-between p-3">
                <div>
                  <p className="font-medium tabular-nums">{formatCents(p.amount_cents)}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {p.method} · {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : 'pending'}
                    {p.reference ? ` · ${p.reference}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {p.stripe_charge_id && p.status === 'succeeded' && (
                    <a
                      // LOW-2: Stripe dashboard URLs use /payments/{pi_id}
                      // OR /charges/{ch_id}. We can't put a charge id under
                      // /payments — that's a bad link.
                      href={
                        p.stripe_payment_intent_id
                          ? `https://dashboard.stripe.com/payments/${p.stripe_payment_intent_id}`
                          : `https://dashboard.stripe.com/charges/${p.stripe_charge_id}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-ink-muted)] text-[var(--text-sm)] underline"
                      title="Refund in Stripe Dashboard. The refund will sync back via webhook."
                    >
                      Refund ↗
                    </a>
                  )}
                  <Badge
                    tone={
                      p.status === 'succeeded'
                        ? 'success'
                        : p.status === 'failed'
                          ? 'danger'
                          : p.status === 'refunded'
                            ? 'warn'
                            : 'neutral'
                    }
                  >
                    {p.status}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChargeStatusBadge({ charge }: { charge: ChargeWithAllocations }) {
  const tone =
    charge.status === 'paid'
      ? 'success'
      : charge.status === 'partial'
        ? 'warn'
        : charge.status === 'pending'
          ? 'info'
          : 'neutral';
  return <Badge tone={tone}>{charge.status}</Badge>;
}
